import type { WeaponClass, RigSize } from './character-roster';
import type { ClipStyle } from '../render/animation-map';
import { N_ } from '../i18n/i18n';

/**
 * Client-side mirror of backend/src/game/dungeon-instance/sim/monster-ai.ts's
 * MonsterArchetype, extended with concrete model/rig/weapon filenames under
 * frontend/public/assets/skeletons/ — same "no shared package between
 * backend/ and frontend/, kept in sync by hand" convention as
 * character-roster.ts. design/gdd/skeleton-ai.md's Bone Warden amendment
 * (2026-08-20).
 */

export type MonsterArchetype = 'RATTLER' | 'BONEWRIGHT' | 'BONE_WARDEN';

export interface MonsterRosterEntry {
  id: MonsterArchetype;
  weaponClass: WeaponClass;
  /** Filename under skeletons/characters/, without extension. */
  characterModel: string;
  rig: RigSize;
  /**
   * Which clip family this body animates with (`render/animation-map.ts`).
   *
   * KayKit Character Animations import (2026-08-25) — the pack ships a
   * Skeletons_* set (its own idle, walk, death, and an awaken for getting up
   * off the floor), which is what a pile of animated bones should move like
   * rather than borrowing a living person's walk cycle. The golem is a
   * large-rig body and the pack has no undead set for that rig, so it stays
   * humanoid.
   */
  clipStyle: ClipStyle;
  /** Filename under skeletons/equipment/, without extension. */
  weaponModel: string;
  /** Filename under skeletons/equipment/, without extension — cosmetic only, same convention as CharacterRosterEntry.shieldModel. */
  shieldModel?: string;
}

/**
 * The model mapping is game-concept.md §5's own parenthetical naming
 * ("Rattlers (minions)", "Bonewrights (warriors)"), not an arbitrary pick.
 * Bone Warden -> golem is the large-rig, boss-tier body the asset pack
 * ships (frontend/public/assets/skeletons/README.md confirms golem alone
 * shares the large rig, by bind-pose comparison, not just joint names).
 */
export const MONSTER_ROSTER: readonly MonsterRosterEntry[] = [
  { id: 'RATTLER', weaponClass: 'LIGHT', characterModel: 'minion', rig: 'medium', clipStyle: 'undead', weaponModel: 'dagger' },
  { id: 'BONEWRIGHT', weaponClass: 'MEDIUM', characterModel: 'warrior', rig: 'medium', clipStyle: 'undead', weaponModel: 'blade', shieldModel: 'shield_small_a' },
  { id: 'BONE_WARDEN', weaponClass: 'HEAVY', characterModel: 'golem', rig: 'large', clipStyle: 'humanoid', weaponModel: 'golem_axe_large' },
];

/**
 * Player-facing names, for the target frame (2026-08-24). The archetype ids
 * are `SCREAMING_SNAKE` constants and reading `BONE_WARDEN` off a health bar
 * is worse than reading nothing. Wording is `game-concept.md` section 5's own.
 */
export const MONSTER_DISPLAY_NAME: Record<MonsterArchetype, string> = {
  RATTLER: N_('Rattler'),
  BONEWRIGHT: N_('Bonewright'),
  BONE_WARDEN: N_('Bone Warden'),
};

export function monsterRosterEntry(id: string): MonsterRosterEntry {
  const found = MONSTER_ROSTER.find((entry) => entry.id === id);
  // Same fallback convention as character-roster.ts's rosterEntry() — an
  // unrecognized archetype (stale data, a future roster entry this build
  // doesn't know about yet) renders as the first entry rather than throwing.
  return found ?? MONSTER_ROSTER[0];
}
